import api from 'services/api';
import { REJECT, array, bool, nullable, num, object, str, union, type Received } from 'validation';
import { readResponse } from 'api/shared/readResponse';

// GET /node_instances/:id/blockers and PUT …/blockers/:blockerId/unblock (routes/node_instances.js).
// Both are boundary-validated here and returned in the backend's snake_case wire shape. The polymorphic
// `details` payload is decoded per blocker type via the `union()` combinator (discriminated on `type`) —
// an unknown blocker type is dropped from the array rather than breaking the whole response.

export type NodeInstanceBlockerType = 'event_wait' | 'scheduled_start_datetime' | 'schedule_start_time';

/** The blocked node instance's own summary. */
export interface NodeInstanceBlockerSummary {
  readonly node_instance_id: number;
  readonly parent_node_instance_id: number | null;
  readonly name: string | null;
  // `node_instances.status` is a nullable column, and this service passes it through raw (`status: node.status`) — unlike
  // the boolean flags on the same rows, which it normalises with `=== true` first.
  readonly status: string | null;
}

export interface EventWaitBlockerDetails {
  readonly node_instance_events_id: number | null;
  readonly event_id: number | null;
  readonly event_name: string;
  readonly event_type: string | null;
  readonly zone_name: string;
  readonly auto_submit: boolean;
  readonly auto_clear: boolean;
  readonly precondition: string | null;
  readonly raised: boolean;
}

export interface ScheduleStartTimeBlockerDetails {
  readonly schedule_start_time: string;
  readonly timezone: string;
  readonly target_datetime: string | null;
  readonly target_time: string | null;
  readonly current_time: string | null;
}

export interface ScheduledStartDatetimeBlockerDetails {
  readonly scheduled_start_datetime: string;
  readonly timezone: string;
  readonly target_datetime: string | null;
  readonly target_time: string | null;
  readonly current_time: string | null;
}

export interface EventWaitBlocker {
  readonly id: string;
  readonly type: 'event_wait';
  readonly actionable: boolean;
  readonly details: EventWaitBlockerDetails;
}

export interface ScheduledStartDatetimeBlocker {
  readonly id: string;
  readonly type: 'scheduled_start_datetime';
  readonly actionable: boolean;
  readonly details: ScheduledStartDatetimeBlockerDetails;
}

export interface ScheduleStartTimeBlocker {
  readonly id: string;
  readonly type: 'schedule_start_time';
  readonly actionable: boolean;
  readonly details: ScheduleStartTimeBlockerDetails;
}

export type NodeInstanceBlocker = EventWaitBlocker | ScheduledStartDatetimeBlocker | ScheduleStartTimeBlocker;

export interface NodeInstanceBlockersResponse {
  readonly node_instance: NodeInstanceBlockerSummary | null;
  readonly blockers: readonly NodeInstanceBlocker[];
}

export interface UnblockNodeInstanceBlockerResponse {
  readonly success: boolean;
  readonly message: string | null;
  readonly remaining_blockers: readonly NodeInstanceBlocker[];
  readonly status_changed: boolean;
}

const summarySchema = object<NodeInstanceBlockerSummary>({
  node_instance_id: num(),
  parent_node_instance_id: nullable(num(REJECT)),
  name: nullable(str()),
  status: nullable(str()),
});

const eventWaitDetailsSchema = object<EventWaitBlockerDetails>({
  node_instance_events_id: nullable(num()),
  event_id: nullable(num()),
  event_name: str(),
  event_type: nullable(str()),
  zone_name: str(),
  auto_submit: bool(),
  auto_clear: bool(),
  precondition: nullable(str()),
  raised: bool(),
});

const scheduleStartTimeDetailsSchema = object<ScheduleStartTimeBlockerDetails>({
  schedule_start_time: str(),
  timezone: str(),
  target_datetime: nullable(str()),
  target_time: nullable(str()),
  current_time: nullable(str()),
});

const scheduledStartDatetimeDetailsSchema = object<ScheduledStartDatetimeBlockerDetails>({
  scheduled_start_datetime: str(),
  timezone: str(),
  target_datetime: nullable(str()),
  target_time: nullable(str()),
  current_time: nullable(str()),
});

// Discriminated on `type`; the branch shape omits `type` (the union re-attaches it) and validates the
// per-type `details`. An unknown discriminator makes the row `ok: false`, so `array()` drops it — a
// blocker kind the FE doesn't model is silently omitted rather than failing the whole response.
const blockerSchema = union<NodeInstanceBlocker, 'type'>('type', {
  event_wait: { id: str(REJECT), actionable: bool(), details: eventWaitDetailsSchema },
  scheduled_start_datetime: { id: str(REJECT), actionable: bool(), details: scheduledStartDatetimeDetailsSchema },
  schedule_start_time: { id: str(REJECT), actionable: bool(), details: scheduleStartTimeDetailsSchema },
});

const blockersResponseSchema = object<NodeInstanceBlockersResponse>({
  node_instance: nullable(summarySchema),
  blockers: array(blockerSchema),
});

const unblockResponseSchema = object<UnblockNodeInstanceBlockerResponse>({
  success: bool(),
  message: nullable(str()),
  remaining_blockers: array(blockerSchema),
  status_changed: bool(),
});

export const getNodeInstanceBlockers = async (nodeInstanceId: number | string): Promise<Received<NodeInstanceBlockersResponse>> => {
  const res = await api.get(`/node_instances/${encodeURIComponent(String(nodeInstanceId))}/blockers`);
  return readResponse(res.data, { schema: blockersResponseSchema, source: 'getNodeInstanceBlockers' });
};

export const unblockNodeInstanceBlocker = async (nodeInstanceId: number | string, blockerId: string): Promise<Received<UnblockNodeInstanceBlockerResponse>> => {
  const res = await api.put(`/node_instances/${encodeURIComponent(String(nodeInstanceId))}/blockers/${encodeURIComponent(blockerId)}/unblock`);
  return readResponse(res.data, { schema: unblockResponseSchema, source: 'unblockNodeInstanceBlocker' });
};
